'use client';

import styles from './InlineEntitySelector.module.css';
import { Dropdown, Flex, ResultListItem, Icon } from '@/_components';
import { FC, useContext, useMemo } from 'react';
import { InlineEntitySelectorProps } from './InlineEntitySelector.types';
import { SelectedEntity } from './components/SelectedEntity';
import { UserContext } from '@/_contexts/UserProvider';
import { getClasses } from '@/_utils/classnames';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { useParams, useRouter } from 'next/navigation';
import { Company } from '@/_types/models/company';

export const InlineEntitySelector: FC<InlineEntitySelectorProps> = () => {
  const { companies, company, selectedCompanyId, updateClientCompany } =
    useContext(UserContext);
  const { previewUserCompanies, previewUserCompanyId, previewCompany } =
    useContext(UserPreviewContext);

  const { previewUserId } = useParams();
  const { push } = useRouter();

  const hasMultipleCompanies: boolean = useMemo(
    () =>
      !!previewUserId
        ? previewUserCompanies?.length > 1
        : companies?.length > 1,
    [companies?.length, previewUserCompanies?.length, previewUserId],
  );

  const dashboardSelectedCompanyId: number | null | undefined = useMemo(
    () => (!!previewUserId ? previewUserCompanyId : selectedCompanyId),
    [previewUserId, previewUserCompanyId, selectedCompanyId],
  );

  const dashboardCompany: Company | undefined = !!previewUserId
    ? previewCompany
    : company;

  const dashboardCompanies: Array<Company> = !!previewUserId
    ? previewUserCompanies
    : companies || [];

  const dropdownItems: Array<ResultListItem> = dashboardCompanies.map(
    (company) => {
      const onCompanyClick = async () => {
        !!previewUserId
          ? push(
              `/preview/user/${previewUserId}?previewCompanyHash=${company.hash_id}`,
            )
          : updateClientCompany(company);
      };

      return {
        icon:
          company.id === dashboardSelectedCompanyId
            ? 'check-circle-filled'
            : 'circle',
        name: company.name,
        value: company.id.toString(),
        onClick: onCompanyClick as any,
      } as ResultListItem;
    },
  );

  if (dashboardCompany?.name && !hasMultipleCompanies) {
    return <SelectedEntity name={dashboardCompany?.name} />;
  }

  if (!hasMultipleCompanies) return null;

  const inlineEntitySelectorClasses: string = getClasses(
    styles.inlineEntitySelector,
  );

  return (
    <Flex isInline={true} className={inlineEntitySelectorClasses}>
      <Dropdown items={dropdownItems} placement="left">
        <Flex gap={2} isYCentered={true}>
          <SelectedEntity
            name={dashboardCompany?.name || 'Please select an Entity'}
          />
          <Icon name="caret-down" size={16} />
        </Flex>
      </Dropdown>
    </Flex>
  );
};
